import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { RolePicker } from '@/components/RolePicker';
import { CustomEmojiPicker } from '@/components/CustomEmojiPicker';
import { EmojiDisplay } from '@/components/EmojiDisplay';
import { MediaPicker } from '@/components/MediaPicker';
import { BackLink } from './_shared';
import { postedKey, type PostedMessage } from '@/lib/posted';
import { PostedList } from '@/components/PostedList';

/**
 * SelfRoles config — two flows:
 *   1. "Neues Panel" (default): the user writes the message text in the browser,
 *      adds N emoji→role bindings, and one click posts the message + seeds all
 *      reactions + stores every binding.
 *   2. "An existierende Nachricht binden" (advanced): the legacy single-binding
 *      form for hooking into a message that already exists.
 *
 * The active-bindings list is grouped by message so it's obvious which panel
 * a binding belongs to.
 */

interface SelfRole {
  channelId: string;
  messageId: string;
  emoji: string;
  roleId: string;
}
interface Channel {
  id: string;
  name: string;
}
interface Role {
  id: string;
  name: string;
}
interface Binding {
  emoji: string | null;
  roleId: string | null;
}

export function SelfRolesConfig() {
  const { guildId } = useParams<{ guildId: string }>();
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['self-roles', guildId],
    queryFn: () => api<{ selfRoles: SelfRole[] }>(`/api/guilds/${guildId}/self-roles`),
    enabled: !!guildId,
  });
  const channelsQuery = useQuery({
    queryKey: ['guild-channels', guildId],
    queryFn: () => api<{ channels: Channel[] }>(`/api/guilds/${guildId}/channels`),
    enabled: !!guildId,
    staleTime: 30_000,
  });
  const rolesQuery = useQuery({
    queryKey: ['guild-roles', guildId],
    queryFn: () => api<{ roles: Role[] }>(`/api/guilds/${guildId}/roles`),
    enabled: !!guildId,
    staleTime: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: (index: number) =>
      api(`/api/guilds/${guildId}/self-roles/${index}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['self-roles', guildId] }),
  });

  // null = not found in Discord (deleted). Only decided once the list has
  // loaded, so nothing flashes "gelöscht" while fetching.
  const channelName = (id: string) =>
    channelsQuery.data ? channelsQuery.data.channels.find((c) => c.id === id)?.name ?? null : '…';
  const roleName = (id: string) =>
    rolesQuery.data ? rolesQuery.data.roles.find((r) => r.id === id)?.name ?? null : '…';
  const missing = (text: string, id: string) => (
    <span className="text-amber-600 dark:text-amber-400" title={`ID ${id}`}>
      {text}
    </span>
  );

  // Group bindings by messageId so panels appear as one card each.
  const groups = useMemo(() => {
    const map = new Map<
      string,
      { messageId: string; channelId: string; bindings: Array<SelfRole & { index: number }> }
    >();
    (listQuery.data?.selfRoles ?? []).forEach((sr, idx) => {
      const bucket = map.get(sr.messageId) ?? {
        messageId: sr.messageId,
        channelId: sr.channelId,
        bindings: [],
      };
      bucket.bindings.push({ ...sr, index: idx });
      map.set(sr.messageId, bucket);
    });
    return [...map.values()];
  }, [listQuery.data]);

  return (
    <div className="mx-auto max-w-3xl">
      <BackLink guildId={guildId!} />
      <h1 className="mb-6 text-2xl font-semibold">Self-Roles — Konfiguration</h1>

      <PanelComposer
        guildId={guildId!}
        onCreated={() => qc.invalidateQueries({ queryKey: ['self-roles', guildId] })}
      />

      <ExistingMessageBinder
        guildId={guildId!}
        onSaved={() => qc.invalidateQueries({ queryKey: ['self-roles', guildId] })}
      />

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">Aktive Panels</CardTitle>
          <CardDescription>
            Alle Reaktions-Rollen, gruppiert nach der Nachricht der sie hängen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading && <p className="text-muted-foreground">Lade…</p>}
          {!listQuery.isLoading && groups.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Self-Roles.</p>
          )}
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.messageId} className="rounded-md border p-3">
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                  <div className="text-sm font-medium">
                    {channelName(g.channelId) === null
                      ? missing('Channel nicht gefunden', g.channelId)
                      : `#${channelName(g.channelId)}`}
                  </div>
                  <code className="text-[10px] text-muted-foreground">
                    Message {g.messageId}
                  </code>
                </div>
                {channelName(g.channelId) === null && (
                  <p className="mb-2 text-xs text-muted-foreground">
                    Das Panel existiert in Discord nicht mehr — die Einträge können gelöscht werden.
                  </p>
                )}
                <ul className="space-y-1.5">
                  {g.bindings.map((b) => (
                    <li
                      key={`${b.messageId}:${b.emoji}`}
                      className="flex items-center justify-between rounded bg-muted/40 px-2 py-1 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <EmojiDisplay value={b.emoji} size={22} />
                        <span>→</span>
                        <span className="font-medium">
                          {roleName(b.roleId) === null
                            ? missing('gelöschte Rolle', b.roleId)
                            : `@${roleName(b.roleId)}`}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={deleteMut.isPending}
                        onClick={() => {
                          if (confirm('Diese Reaktions-Rolle entfernen?')) {
                            deleteMut.mutate(b.index);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// --------------------------- Panel Composer ---------------------------

function PanelComposer({
  guildId,
  onCreated,
}: {
  guildId: string;
  onCreated: () => void;
}) {
  const [channelId, setChannelId] = useState<string | null>(null);
  const [content, setContent] = useState('React mit einem Emoji unten, um die entsprechende Rolle zu bekommen:');
  const [imageUrl, setImageUrl] = useState('');
  const [bindings, setBindings] = useState<Binding[]>([{ emoji: null, roleId: null }]);
  const [mode, setMode] = useState<'reactions' | 'buttons' | 'select'>('reactions');
  /** Only one role of this panel at a time (e.g. colour roles). */
  const [exclusive, setExclusive] = useState(false);
  /** "Leise senden": mentions in the text show, nobody gets pinged. */
  const [silent, setSilent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const qc = useQueryClient();

  /** The composer as panel data — posted as-is, and what "Aktualisieren" sends. */
  const panelData = () => ({
    content: content.trim(),
    imageUrl: imageUrl || undefined,
    mode,
    exclusive,
    silent,
    bindings: bindings
      .filter((b) => b.emoji && b.roleId)
      .map((b) => ({ emoji: b.emoji, roleId: b.roleId })),
  });

  /** "Laden": an already posted panel back into the composer. */
  const load = (p: PostedMessage) => {
    const d = p.data as {
      content?: string;
      imageUrl?: string | null;
      mode?: 'reactions' | 'buttons' | 'select';
      exclusive?: boolean;
      silent?: boolean;
      bindings?: Array<{ emoji: string; roleId: string }>;
    };
    setContent(d.content ?? '');
    setImageUrl(d.imageUrl ?? '');
    setMode(d.mode ?? 'reactions');
    setExclusive(!!d.exclusive);
    setSilent(!!d.silent);
    setBindings(d.bindings?.length ? d.bindings.map((b) => ({ emoji: b.emoji, roleId: b.roleId })) : [{ emoji: null, roleId: null }]);
    setLoaded(true);
  };

  const postMut = useMutation({
    mutationFn: () =>
      api<{ messageId: string; reactionWarnings?: string[] }>(
        `/api/guilds/${guildId}/self-roles/panel`,
        {
          method: 'POST',
          body: JSON.stringify({ channelId, ...panelData() }),
        }
      ),
    onSuccess: (res) => {
      setError(null);
      setWarnings(res.reactionWarnings ?? null);
      setBindings([{ emoji: null, roleId: null }]);
      onCreated();
      qc.invalidateQueries({ queryKey: postedKey(guildId) });
    },
    onError: (err: Error) => {
      setError(err.message);
      setWarnings(null);
    },
  });

  const validBindings = bindings.filter((b) => b.emoji && b.roleId);
  const canSubmit =
    !!channelId &&
    content.trim().length > 0 &&
    validBindings.length > 0 &&
    !postMut.isPending;

  const update = (i: number, patch: Partial<Binding>) =>
    setBindings((prev) => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-lg">Neues Reaktions-Panel</CardTitle>
        <CardDescription>
          Schreib den Text der Nachricht direkt hier, füge Emoji→Rollen-Bindings hinzu
          und klick auf „Panel posten". Der Bot postet die Nachricht in den gewählten
          Channel und setzt alle Reactions automatisch.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Ziel-Channel</Label>
          <ChannelPicker
            guildId={guildId}
            value={channelId}
            onChange={setChannelId}
            types={[CHANNEL_TYPE.GUILD_TEXT, CHANNEL_TYPE.GUILD_ANNOUNCEMENT]}
          />
        </div>
        <div>
          <Label>Nachrichten-Text</Label>
          <Textarea emoji
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-[100px]"
            maxLength={2000}
            placeholder="Text der Nachricht, die im Channel erscheint…"
          />
          <div className="mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <Switch
              checked={silent}
              onChange={(e) => setSilent(e.target.checked)}
              label="Leise senden (niemand wird gepingt)"
              className="text-xs"
            />
            <span>{content.length} / 2000</span>
          </div>
        </div>
        <div>
          <Label>Großes Bild URL (optional)</Label>
          <div className="flex gap-2">
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://…"
            />
            <MediaPicker
              guildId={guildId}
              currentUrl={imageUrl}
              onSelect={(url) => setImageUrl(window.location.origin + url)}
            />
          </div>
        </div>

        <div>
          <Label>Interaktions-Modus</Label>
          <div className="mt-1 flex flex-wrap gap-1">
            {(['reactions', 'buttons', 'select'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={
                  'rounded-md border px-3 py-1.5 text-xs ' +
                  (mode === m
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'bg-background hover:bg-accent')
                }
              >
                {m === 'reactions' ? '🔴 Reactions' : m === 'buttons' ? '🔘 Buttons' : '📋 Select-Menü'}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {mode === 'reactions'
              ? 'Bot reagiert mit Emoji, User re-reactet zum Toggeln (Legacy).'
              : mode === 'buttons'
              ? 'Ein Button pro Rolle. Klick = Rolle togglen (add/remove).'
              : 'Ein Dropdown-Menü mit allen Rollen. Auswahl = Rolle togglen.'}
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
          <div className="min-w-0">
            <div className={exclusive ? 'text-sm' : 'text-sm text-muted-foreground'}>Nur eine Rolle wählbar</div>
            <div className="text-[11px] text-muted-foreground">
              Z. B. für Farbrollen: Wer eine Rolle aus diesem Panel wählt, verliert die anderen aus dem Panel.
              Erneut anklicken nimmt sie wieder weg.
              {exclusive && ' Tipp: Die Farbe sieht man nur, wenn die Rolle in Discord über anderen farbigen Rollen steht.'}
            </div>
          </div>
          <Switch
            checked={exclusive}
            onChange={(e) => setExclusive(e.target.checked)}
            aria-label="Nur eine Rolle wählbar"
          />
        </div>

        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <Label>Reaktions-Rollen ({validBindings.length}/{bindings.length})</Label>
            <span className="text-xs text-muted-foreground">bis 25</span>
          </div>
          <div className="space-y-2">
            {bindings.map((b, i) => (
              <div
                key={i}
                className="flex flex-wrap items-center gap-2 rounded-md border p-2"
              >
                <CustomEmojiPicker
                  value={b.emoji}
                  onChange={(v) => update(i, { emoji: v })}
                />
                <span className="text-muted-foreground">→</span>
                <div className="min-w-[200px] flex-1">
                  <RolePicker
                    guildId={guildId}
                    value={b.roleId}
                    onChange={(v) => update(i, { roleId: v })}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    setBindings((prev) => prev.filter((_, j) => j !== i))
                  }
                  disabled={bindings.length === 1}
                  title="Zeile entfernen"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {bindings.length < 25 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setBindings((prev) => [...prev, { emoji: null, roleId: null }])
                }
              >
                <Plus className="mr-1 h-3 w-3" /> Weitere Rolle
              </Button>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {warnings && warnings.length > 0 && (
          <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-2 text-xs">
            <div className="mb-1 font-medium">
              Panel wurde gepostet, aber einige Reactions konnten nicht gesetzt werden:
            </div>
            <ul className="list-disc pl-4">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
        {postMut.isSuccess && !warnings && (
          <p className="text-sm text-success">Panel gepostet ✓</p>
        )}

        <div className="flex justify-end">
          <Button disabled={!canSubmit} onClick={() => postMut.mutate()}>
            {postMut.isPending ? 'Poste…' : 'Panel posten'}
          </Button>
        </div>

        <div className="border-t pt-4">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Bereits gepostet
          </div>
          <p className="mb-2 text-xs text-muted-foreground">
            {loaded
              ? 'Panel geladen — oben ändern und beim Post „Aktualisieren“ klicken. Die Rollen-Zuordnungen werden mit ersetzt.'
              : '„Laden“ holt ein Panel in den Editor, „Aktualisieren“ überschreibt es mit dem Editor (inkl. Rollen).'}
          </p>
          <PostedList
            guildId={guildId}
            type="self-roles"
            getUpdateData={panelData}
            onLoad={load}
            onChanged={onCreated}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// --------------------- Bind existing message (advanced) ---------------------

function ExistingMessageBinder({
  guildId,
  onSaved,
}: {
  guildId: string;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [messageId, setMessageId] = useState('');
  const [emoji, setEmoji] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addMut = useMutation({
    mutationFn: (body: { channelId: string; messageId: string; emoji: string; roleId: string }) =>
      api(`/api/guilds/${guildId}/self-roles`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setChannelId(null);
      setMessageId('');
      setEmoji(null);
      setRoleId(null);
      setError(null);
      onSaved();
    },
    onError: (err: Error) => setError(err.message),
  });

  const canSubmit = channelId && messageId && emoji && roleId && !addMut.isPending;

  if (!open) {
    return (
      <Card className="mb-4">
        <CardContent className="pt-6">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ▸ An existierende Nachricht binden (Advanced) — nur wenn du bereits eine Nachricht gepostet hast
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-baseline justify-between">
          <CardTitle className="text-lg">An existierende Nachricht binden</CardTitle>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ▾ zuklappen
          </button>
        </div>
        <CardDescription>
          Für Nachrichten die bereits im Channel stehen — Message-ID in Discord per Rechtsklick → „ID kopieren".
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Channel der Nachricht</Label>
          <ChannelPicker
            guildId={guildId}
            value={channelId}
            onChange={setChannelId}
            types={[CHANNEL_TYPE.GUILD_TEXT, CHANNEL_TYPE.GUILD_ANNOUNCEMENT]}
          />
        </div>
        <div>
          <Label>Message-ID</Label>
          <Input
            value={messageId}
            onChange={(e) => setMessageId(e.target.value.trim())}
            placeholder="z.B. 1234567890123456789"
          />
        </div>
        <div>
          <Label>Emoji</Label>
          <CustomEmojiPicker value={emoji} onChange={setEmoji} />
        </div>
        <div>
          <Label>Rolle</Label>
          <RolePicker guildId={guildId} value={roleId} onChange={setRoleId} />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end">
          <Button
            disabled={!canSubmit}
            onClick={() =>
              addMut.mutate({
                channelId: channelId!,
                messageId,
                emoji: emoji!,
                roleId: roleId!,
              })
            }
          >
            {addMut.isPending ? 'Speichere…' : 'Binden'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
